'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown, Edit2, Trash2, Eye, ChevronLeft, ChevronRight, X, ArrowUpRight, UserCheck } from 'lucide-react';
import Badge from '@/components/ui/Badge';
import Modal from '@/components/ui/Modal';
import { projects, stageVariant, priorityVariant, type Project, type ProjectStage } from './projectsData';
import BulkAssignModal from './BulkAssignModal';

type SortKey = keyof Project;
type SortDir = 'asc' | 'desc' | null;

const STAGES: ProjectStage[] = [
  'Inquiry', 'Requirement', 'Proposal', 'Approval',
  'Development', 'Testing', 'Client Review', 'Deployment', 'Maintenance',
];

const SERVICE_TYPES = [
  'Web Development', 'Mobile App', 'ERP / CRM', 'Custom Software',
  'BPO Services', 'Digital Marketing', 'Cloud / Hosting', 'API Integration',
];

const PRIORITIES = ['Critical', 'High', 'Medium', 'Low'];

const PAGE_SIZES = [10, 25, 50];

export default function ProjectsTable() {
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState<string>('All');
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [priorityFilter, setPriorityFilter] = useState<string>('All');
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [stageDropdownFor, setStageDropdownFor] = useState<string | null>(null);
  const [deleteModal, setDeleteModal] = useState<Project | null>(null);
  const [tableData, setTableData] = useState<Project[]>(projects);
  const [assignModalOpen, setAssignModalOpen] = useState(false);

  const filtered = useMemo(() => {
    let d = tableData;
    if (search) {
      const q = search.toLowerCase();
      d = d.filter((p) => p.name.toLowerCase().includes(q) || p.client.toLowerCase().includes(q) || p.pm.toLowerCase().includes(q));
    }
    if (stageFilter !== 'All') d = d.filter((p) => p.stage === stageFilter);
    if (typeFilter !== 'All') d = d.filter((p) => p.serviceType === typeFilter);
    if (priorityFilter !== 'All') d = d.filter((p) => p.priority === priorityFilter);

    if (sortKey && sortDir) {
      d = [...d].sort((a, b) => {
        const av = a[sortKey];
        const bv = b[sortKey];
        if (typeof av === 'number' && typeof bv === 'number') {
          return sortDir === 'asc' ? av - bv : bv - av;
        }
        return sortDir === 'asc'
          ? String(av).localeCompare(String(bv))
          : String(bv).localeCompare(String(av));
      });
    }
    return d;
  }, [tableData, search, stageFilter, typeFilter, priorityFilter, sortKey, sortDir]);

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : d === 'desc' ? null : 'asc'));
      if (sortDir === 'desc') setSortKey(null);
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={12} className="text-muted-foreground/50" />;
    if (sortDir === 'asc') return <ChevronUp size={12} className="text-primary" />;
    return <ChevronDown size={12} className="text-primary" />;
  };

  const toggleSelect = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (selected.size === paginated.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(paginated.map((p) => p.id)));
    }
  };

  const handleStageChange = (projectId: string, newStage: ProjectStage) => {
    setTableData((prev) =>
      prev.map((p) => (p.id === projectId ? { ...p, stage: newStage } : p))
    );
    setStageDropdownFor(null);
    toast.success(`Stage updated to "${newStage}"`);
  };

  const handleDelete = (project: Project) => {
    setTableData((prev) => prev.filter((p) => p.id !== project.id));
    setDeleteModal(null);
    toast.success(`Project "${project.name}" deleted`);
  };

  const handleBulkDelete = () => {
    setTableData((prev) => prev.filter((p) => !selected.has(p.id)));
    toast.success(`${selected.size} project${selected.size > 1 ? 's' : ''} deleted`);
    setSelected(new Set());
  };

  const handleBulkAssign = (memberIds: string[], memberNames: string[], notify: boolean) => {
    const count = selected.size;
    const names = memberNames.join(', ');
    toast.success(
      `${count} project${count > 1 ? 's' : ''} assigned to ${names}${notify ? ' · Notifications sent' : ''}`
    );
    setSelected(new Set());
  };

  const completionColor = (pct: number) => {
    if (pct >= 90) return 'bg-success';
    if (pct >= 50) return 'bg-primary';
    if (pct >= 20) return 'bg-warning';
    return 'bg-danger';
  };

  const deadlineDisplay = (project: Project) => {
    if (project.deadline === 'Ongoing' || project.deadline === 'TBD') return project.deadline;
    if (project.daysLeft < 0) return <span className="text-danger font-medium">{Math.abs(project.daysLeft)}d overdue</span>;
    if (project.daysLeft <= 14) return <span className="text-warning font-medium">{project.daysLeft}d left</span>;
    return <span className="text-muted-foreground">{project.daysLeft}d left</span>;
  };

  return (
    <>
      {/* Filters */}
      <div className="card-surface mb-4 p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              placeholder="Search projects, clients, PMs…"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              className="w-full pl-9 pr-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent transition-all"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              value={stageFilter}
              onChange={(e) => { setStageFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            >
              <option value="All">All Stages</option>
              {STAGES.map((s) => <option key={`stage-opt-${s}`} value={s}>{s}</option>)}
            </select>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            >
              <option value="All">All Service Types</option>
              {SERVICE_TYPES.map((s) => <option key={`type-opt-${s}`} value={s}>{s}</option>)}
            </select>
            <select
              value={priorityFilter}
              onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg bg-input border border-border text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring transition-all"
            >
              <option value="All">All Priorities</option>
              {PRIORITIES.map((p) => <option key={`priority-opt-${p}`} value={p}>{p}</option>)}
            </select>
            {(search || stageFilter !== 'All' || typeFilter !== 'All' || priorityFilter !== 'All') && (
              <button
                onClick={() => { setSearch(''); setStageFilter('All'); setTypeFilter('All'); setPriorityFilter('All'); setPage(1); }}
                className="flex items-center gap-1 px-3 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-danger hover:border-danger/40 transition-all"
              >
                <X size={13} /> Clear
              </button>
            )}
          </div>
          <div className="sm:ml-auto text-xs text-muted-foreground flex items-center">
            {filtered.length} of {tableData.length} projects
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card-surface overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="border-b border-border bg-muted/20">
                <th className="px-4 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={paginated.length > 0 && selected.size === paginated.length}
                    onChange={selectAll}
                    className="w-4 h-4 rounded border-border bg-input accent-primary"
                    aria-label="Select all rows"
                  />
                </th>
                {[
                  { key: 'name' as SortKey, label: 'Project' },
                  { key: 'client' as SortKey, label: 'Client' },
                  { key: 'serviceType' as SortKey, label: 'Service Type' },
                  { key: 'stage' as SortKey, label: 'Stage' },
                  { key: 'pm' as SortKey, label: 'Project Manager' },
                  { key: 'teamSize' as SortKey, label: 'Team' },
                  { key: 'budget' as SortKey, label: 'Budget' },
                  { key: 'daysLeft' as SortKey, label: 'Deadline' },
                  { key: 'completion' as SortKey, label: 'Progress' },
                  { key: 'priority' as SortKey, label: 'Priority' },
                ].map((col) => (
                  <th
                    key={`th-${col.key}`}
                    className="px-4 py-3 text-left text-2xs font-semibold uppercase tracking-wider text-muted-foreground cursor-pointer hover:text-foreground transition-colors select-none"
                    onClick={() => handleSort(col.key)}
                  >
                    <div className="flex items-center gap-1.5">
                      {col.label}
                      <SortIcon col={col.key} />
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 text-left text-2xs font-semibold uppercase tracking-wider text-muted-foreground w-20">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={12} className="text-center py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-12 h-12 rounded-xl bg-muted flex items-center justify-center">
                        <Search size={20} className="text-muted-foreground" />
                      </div>
                      <p className="text-sm font-medium text-foreground">No projects match your filters</p>
                      <p className="text-xs text-muted-foreground">Try adjusting the search or filter criteria</p>
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((project) => (
                  <tr
                    key={project.id}
                    className={`border-b border-border/50 hover:bg-muted/20 transition-colors group ${selected.has(project.id) ? 'bg-primary/5' : ''}`}
                  >
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selected.has(project.id)}
                        onChange={() => toggleSelect(project.id)}
                        className="w-4 h-4 rounded border-border bg-input accent-primary"
                        aria-label={`Select ${project.name}`}
                      />
                    </td>

                    {/* Project Name */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-foreground truncate max-w-[160px]">{project.name}</p>
                          <p className="text-2xs text-muted-foreground font-mono">{project.id}</p>
                        </div>
                      </div>
                    </td>

                    {/* Client */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-foreground">{project.client}</span>
                    </td>

                    {/* Service Type */}
                    <td className="px-4 py-3">
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{project.serviceType}</span>
                    </td>

                    {/* Stage — inline dropdown */}
                    <td className="px-4 py-3 relative">
                      <button
                        onClick={() => setStageDropdownFor(stageDropdownFor === project.id ? null : project.id)}
                        className="focus:outline-none"
                        aria-label={`Change stage for ${project.name}`}
                      >
                        <Badge
                          label={project.stage}
                          variant={stageVariant[project.stage] as Parameters<typeof Badge>[0]['variant']}
                        />
                      </button>
                      {stageDropdownFor === project.id && (
                        <div className="absolute z-30 left-0 top-full mt-1 card-elevated shadow-xl min-w-[160px] py-1 animate-slide-up">
                          {STAGES.map((s) => (
                            <button
                              key={`stage-dd-${project.id}-${s}`}
                              onClick={() => handleStageChange(project.id, s)}
                              className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted/50 transition-colors ${s === project.stage ? 'text-primary font-medium' : 'text-foreground'}`}
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>

                    {/* PM */}
                    <td className="px-4 py-3">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">{project.pm}</span>
                    </td>

                    {/* Team */}
                    <td className="px-4 py-3 text-center">
                      <span className="text-sm font-tabular text-foreground">{project.teamSize > 0 ? project.teamSize : '—'}</span>
                    </td>

                    {/* Budget */}
                    <td className="px-4 py-3">
                      <span className="text-sm font-tabular text-foreground whitespace-nowrap">{project.budget}</span>
                    </td>

                    {/* Deadline */}
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-xs">{deadlineDisplay(project)}</p>
                        {project.deadline !== 'Ongoing' && project.deadline !== 'TBD' && (
                          <p className="text-2xs text-muted-foreground">{project.deadline}</p>
                        )}
                      </div>
                    </td>

                    {/* Progress */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 min-w-[80px]">
                        <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${completionColor(project.completion)}`}
                            style={{ width: `${project.completion}%` }}
                          />
                        </div>
                        <span className="text-xs font-tabular text-muted-foreground w-8 text-right">{project.completion}%</span>
                      </div>
                    </td>

                    {/* Priority */}
                    <td className="px-4 py-3">
                      <Badge
                        label={project.priority}
                        variant={priorityVariant[project.priority] as Parameters<typeof Badge>[0]['variant']}
                      />
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                          title="View project details"
                          aria-label={`View ${project.name}`}
                        >
                          <Eye size={14} />
                        </button>
                        <button
                          className="p-1.5 rounded-md hover:bg-muted transition-colors text-muted-foreground hover:text-primary"
                          title="Edit project"
                          aria-label={`Edit ${project.name}`}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={() => setDeleteModal(project)}
                          className="p-1.5 rounded-md hover:bg-danger/10 transition-colors text-muted-foreground hover:text-danger"
                          title="Delete project — this cannot be undone"
                          aria-label={`Delete ${project.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-border">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <span>Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
              className="px-2 py-1 rounded-md bg-input border border-border text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {PAGE_SIZES.map((s) => <option key={`ps-${s}`} value={s}>{s}</option>)}
            </select>
            <span>·</span>
            <span>{filtered.length} total results</span>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Previous page"
            >
              <ChevronLeft size={15} />
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
              .reduce<Array<number | 'ellipsis'>>((acc, p, idx, arr) => {
                if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                acc.push(p);
                return acc;
              }, [])
              .map((item, idx) =>
                item === 'ellipsis' ? (
                  <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground text-sm">…</span>
                ) : (
                  <button
                    key={`page-${item}`}
                    onClick={() => setPage(item as number)}
                    className={`w-8 h-8 rounded-md text-sm font-medium transition-colors ${page === item ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'}`}
                  >
                    {item}
                  </button>
                )
              )}
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages || totalPages === 0}
              className="p-1.5 rounded-md hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-muted-foreground hover:text-foreground"
              aria-label="Next page"
            >
              <ChevronRight size={15} />
            </button>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 animate-slide-up">
          <div className="card-elevated px-5 py-3 flex items-center gap-4 shadow-2xl">
            <span className="text-sm font-medium text-foreground">
              {selected.size} project{selected.size > 1 ? 's' : ''} selected
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setAssignModalOpen(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 active:scale-95 transition-all"
              >
                <UserCheck size={13} />
                Assign
              </button>
              <button
                onClick={() => toast.info(`Exporting ${selected.size} projects…`)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-muted text-sm text-foreground hover:bg-muted/80 transition-all"
              >
                <ArrowUpRight size={13} />
                Export
              </button>
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-danger/10 text-sm text-danger hover:bg-danger/20 transition-all"
              >
                <Trash2 size={13} />
                Delete selected
              </button>
              <button
                onClick={() => setSelected(new Set())}
                className="p-1.5 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                aria-label="Clear selection"
              >
                <X size={15} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Assign Modal */}
      <BulkAssignModal
        open={assignModalOpen}
        onClose={() => setAssignModalOpen(false)}
        selectedCount={selected.size}
        selectedProjectNames={tableData.filter((p) => selected.has(p.id)).map((p) => p.name)}
        onAssign={handleBulkAssign}
      />

      {/* Delete confirm modal */}
      <Modal
        open={!!deleteModal}
        onClose={() => setDeleteModal(null)}
        title="Delete Project"
        size="sm"
      >
        {deleteModal && (
          <div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-foreground">{deleteModal.name}</span>?
              This will permanently remove the project and all associated data. This action cannot be undone.
            </p>
            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                onClick={() => setDeleteModal(null)}
                className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:border-primary/40 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteModal)}
                className="px-4 py-2 rounded-lg bg-danger text-white text-sm font-medium hover:bg-danger/90 active:scale-95 transition-all"
              >
                Delete Project
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Stage dropdown backdrop */}
      {stageDropdownFor && (
        <div
          className="fixed inset-0 z-20"
          onClick={() => setStageDropdownFor(null)}
        />
      )}
    </>
  );
}